import {
  pgTable,
  pgEnum,
  uuid,
  varchar,
  text,
  boolean,
  timestamp,
  decimal,
  date,
  char,
  serial,
  integer,
  jsonb,
  bigint,
  index,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const planEnum = pgEnum('plan', ['free', 'premium', 'unlimited']);

export const alertStateEnum = pgEnum('alert_state', [
  'none',
  'good',
  'rare',
  'exceptional',
]);

export const notificationTypeEnum = pgEnum('notification_type', [
  'good',
  'rare',
  'exceptional',
]);

export const notificationChannelEnum = pgEnum('notification_channel', [
  'email',
  'push',
  'in_app',
]);

export const roleEnum = pgEnum('user_role', ['user', 'admin', 'super_admin']);

export const monitoringTypeEnum = pgEnum('monitoring_type', ['fisso', 'flessibile']);

export const affiliateProviderEnum = pgEnum('affiliate_provider', ['kiwi', 'skyscanner', 'direct', 'hotel_crosssell', 'duffel']);

export const cabinClassEnum = pgEnum('cabin_class', ['economy', 'premium_economy', 'business', 'first']);

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export const users = pgTable('users', {
  id: uuid('id').primaryKey().defaultRandom(),
  email: varchar('email', { length: 255 }).unique().notNull(),
  passwordHash: text('password_hash'),
  name: varchar('name', { length: 100 }),
  plan: planEnum('plan').default('free').notNull(),
  stripeCustomerId: varchar('stripe_customer_id'),
  locale: varchar('locale', { length: 5 }).default('it').notNull(),
  role: roleEnum('role').default('user').notNull(),
  onesignalPlayerId: varchar('onesignal_player_id', { length: 64 }),
  banned: boolean('banned').default(false).notNull(),
  emailVerified: boolean('email_verified').default(false).notNull(),
  referralCode: varchar('referral_code', { length: 12 }).unique(),
  referredByUserId: uuid('referred_by_user_id'),
  premiumUntil: timestamp('premium_until', { withTimezone: true }),
  oauthSub: varchar('oauth_sub', { length: 255 }).unique(),
  createdAt: timestamp('created_at', { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const invites = pgTable(
  'invites',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    inviterId: uuid('inviter_id')
      .references(() => users.id, { onDelete: 'cascade' })
      .notNull(),
    inviteeId: uuid('invitee_id')
      .references(() => users.id, { onDelete: 'cascade' })
      .notNull(),
    createdAt: timestamp('created_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index('invites_inviter_idx').on(table.inviterId),
    uniqueIndex('invites_inviter_invitee_idx').on(table.inviterId, table.inviteeId),
  ],
);

export const monitoraggi = pgTable(
  'monitoraggi',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .references(() => users.id, { onDelete: 'cascade' })
      .notNull(),
    aeroportoPartenza: char('aeroporto_partenza', { length: 3 }).notNull(),
    aeroportoArrivo: char('aeroporto_arrivo', { length: 3 }).notNull(),
    dataAndata: date('data_andata').notNull(),
    dataRitorno: date('data_ritorno'),
    soloAndata: boolean('solo_andata').default(false).notNull(),
    tipoMonitoraggio: monitoringTypeEnum('tipo_monitoraggio').default('fisso').notNull(),
    periodoInizio: date('periodo_inizio'),
    periodoFine: date('periodo_fine'),
    soggiornoMin: integer('soggiorno_min'),
    soggiornoMax: integer('soggiorno_max'),
    budgetMax: decimal('budget_max', { precision: 10, scale: 2 }),
    classeCabina: cabinClassEnum('classe_cabina'),
    passeggeri: integer('passeggeri').default(1).notNull(),
    ultimoPrezzo: decimal('ultimo_prezzo', { precision: 10, scale: 2 }),
    mediaStorica: decimal('media_storica', { precision: 10, scale: 2 }),
    statoAlert: alertStateEnum('stato_alert').default('none').notNull(),
    attivo: boolean('attivo').default(true).notNull(),
    notificheAttive: boolean('notifiche_attive').default(true).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index('monitoraggi_user_id_idx').on(table.userId),
    index('monitoraggi_attivo_idx').on(table.attivo),
  ],
);

export const priceHistory = pgTable(
  'price_history',
  {
    id: serial('id').primaryKey(),
    monitoraggioId: uuid('monitoraggio_id')
      .references(() => monitoraggi.id, { onDelete: 'cascade' })
      .notNull(),
    prezzo: decimal('prezzo', { precision: 10, scale: 2 }).notNull(),
    currency: char('currency', { length: 3 }).default('EUR').notNull(),
    compagnia: varchar('compagnia', { length: 100 }),
    source: varchar('source', { length: 50 }).default('travelpayouts').notNull(),
    rawResponse: jsonb('raw_response'),
    checkedAt: timestamp('checked_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index('price_history_monitoraggio_checked_idx').on(
      table.monitoraggioId,
      table.checkedAt,
    ),
  ],
);

export const notifications = pgTable(
  'notifications',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .references(() => users.id, { onDelete: 'cascade' })
      .notNull(),
    monitoraggioId: uuid('monitoraggio_id')
      .references(() => monitoraggi.id, { onDelete: 'cascade' })
      .notNull(),
    tipo: notificationTypeEnum('tipo').notNull(),
    canale: notificationChannelEnum('canale').notNull(),
    titolo: text('titolo').notNull(),
    corpo: text('corpo').notNull(),
    actionUrl: text('action_url'),
    inviataAt: timestamp('inviata_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
    letta: boolean('letta').default(false).notNull(),
  },
  (table) => [
    index('notifications_user_letta_idx').on(table.userId, table.letta),
  ],
);

export const subscriptions = pgTable('subscriptions', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id')
    .references(() => users.id, { onDelete: 'cascade' })
    .unique()
    .notNull(),
  stripeSubscriptionId: varchar('stripe_subscription_id'),
  plan: planEnum('plan').notNull(),
  status: varchar('status', { length: 50 }).default('active').notNull(),
  currentPeriodStart: timestamp('current_period_start', {
    withTimezone: true,
  }),
  currentPeriodEnd: timestamp('current_period_end', { withTimezone: true }),
  createdAt: timestamp('created_at', { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const discountCodes = pgTable('discount_codes', {
  id: uuid('id').primaryKey().defaultRandom(),
  code: varchar('code', { length: 50 }).unique().notNull(),
  description: text('description'),
  discountPercent: decimal('discount_percent', {
    precision: 5,
    scale: 2,
  }).notNull(),
  maxUses: integer('max_uses'),
  currentUses: integer('current_uses').default(0).notNull(),
  validFrom: timestamp('valid_from', { withTimezone: true }).notNull(),
  validUntil: timestamp('valid_until', { withTimezone: true }).notNull(),
  applicablePlans: jsonb('applicable_plans'),
  active: boolean('active').default(true).notNull(),
  createdBy: uuid('created_by').references(() => users.id),
  createdAt: timestamp('created_at', { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const discountRedemptions = pgTable(
  'discount_redemptions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    discountCodeId: uuid('discount_code_id')
      .references(() => discountCodes.id, { onDelete: 'cascade' })
      .notNull(),
    userId: uuid('user_id')
      .references(() => users.id, { onDelete: 'cascade' })
      .notNull(),
    redeemedAt: timestamp('redeemed_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex('discount_redemptions_code_user_idx').on(
      table.discountCodeId,
      table.userId,
    ),
  ],
);

export const errorLogs = pgTable(
  'error_logs',
  {
    id: serial('id').primaryKey(),
    source: varchar('source', { length: 50 }).notNull(),
    severity: varchar('severity', { length: 20 }).notNull(),
    message: text('message').notNull(),
    stack: text('stack'),
    metadata: jsonb('metadata'),
    createdAt: timestamp('created_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index('error_logs_source_created_at_idx').on(
      table.source,
      table.createdAt,
    ),
    index('error_logs_severity_created_at_idx').on(
      table.severity,
      table.createdAt,
    ),
  ],
);

export const referralClicks = pgTable(
  'referral_clicks',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id').references(() => users.id, { onDelete: 'set null' }),
    monitoraggioId: uuid('monitoraggio_id').references(() => monitoraggi.id, { onDelete: 'set null' }),
    provider: affiliateProviderEnum('provider').notNull(),
    flightOrigin: char('flight_origin', { length: 3 }),
    flightDestination: char('flight_destination', { length: 3 }),
    flightPrice: decimal('flight_price', { precision: 10, scale: 2 }),
    affiliateUrl: text('affiliate_url').notNull(),
    clickedAt: timestamp('clicked_at', { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    index('referral_clicks_user_idx').on(table.userId),
    index('referral_clicks_provider_clicked_idx').on(table.provider, table.clickedAt),
  ],
);

export const referralBookings = pgTable(
  'referral_bookings',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    clickId: uuid('click_id').references(() => referralClicks.id, { onDelete: 'set null' }),
    userId: uuid('user_id').references(() => users.id, { onDelete: 'set null' }),
    provider: affiliateProviderEnum('provider').notNull(),
    bookingReference: varchar('booking_reference', { length: 100 }),
    bookingAmount: decimal('booking_amount', { precision: 10, scale: 2 }),
    commissionAmount: decimal('commission_amount', { precision: 10, scale: 2 }),
    currency: char('currency', { length: 3 }).default('EUR').notNull(),
    bookedAt: timestamp('booked_at', { withTimezone: true }).defaultNow().notNull(),
    confirmedAt: timestamp('confirmed_at', { withTimezone: true }),
  },
  (table) => [
    index('referral_bookings_provider_booked_idx').on(table.provider, table.bookedAt),
  ],
);

export const telegramUsers = pgTable(
  'telegram_users',
  {
    telegramChatId: bigint('telegram_chat_id', { mode: 'number' }).primaryKey(),
    userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }),
    accessToken: text('access_token'),
    refreshToken: text('refresh_token'),
    locale: varchar('locale', { length: 5 }).default('it'),
    state: varchar('state', { length: 50 }),
    stateData: jsonb('state_data'),
    createdAt: timestamp('created_at', { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index('telegram_users_user_id_idx').on(table.userId),
  ],
);

// ---------------------------------------------------------------------------
// Relations
// ---------------------------------------------------------------------------

export const usersRelations = relations(users, ({ many, one }) => ({
  monitoraggi: many(monitoraggi),
  notifications: many(notifications),
  subscription: one(subscriptions, {
    fields: [users.id],
    references: [subscriptions.userId],
  }),
  discountRedemptions: many(discountRedemptions),
  createdDiscountCodes: many(discountCodes),
  sentInvites: many(invites),
}));

export const invitesRelations = relations(invites, ({ one }) => ({
  inviter: one(users, { fields: [invites.inviterId], references: [users.id] }),
  invitee: one(users, { fields: [invites.inviteeId], references: [users.id] }),
}));

export const monitoraggiRelations = relations(monitoraggi, ({ one, many }) => ({
  user: one(users, {
    fields: [monitoraggi.userId],
    references: [users.id],
  }),
  priceHistory: many(priceHistory),
  notifications: many(notifications),
}));

export const priceHistoryRelations = relations(priceHistory, ({ one }) => ({
  monitoraggio: one(monitoraggi, {
    fields: [priceHistory.monitoraggioId],
    references: [monitoraggi.id],
  }),
}));

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
  monitoraggio: one(monitoraggi, {
    fields: [notifications.monitoraggioId],
    references: [monitoraggi.id],
  }),
}));

export const subscriptionsRelations = relations(subscriptions, ({ one }) => ({
  user: one(users, {
    fields: [subscriptions.userId],
    references: [users.id],
  }),
}));

export const discountCodesRelations = relations(
  discountCodes,
  ({ one, many }) => ({
    creator: one(users, {
      fields: [discountCodes.createdBy],
      references: [users.id],
    }),
    redemptions: many(discountRedemptions),
  }),
);

export const discountRedemptionsRelations = relations(
  discountRedemptions,
  ({ one }) => ({
    discountCode: one(discountCodes, {
      fields: [discountRedemptions.discountCodeId],
      references: [discountCodes.id],
    }),
    user: one(users, {
      fields: [discountRedemptions.userId],
      references: [users.id],
    }),
  }),
);

export const referralClicksRelations = relations(referralClicks, ({ one }) => ({
  user: one(users, { fields: [referralClicks.userId], references: [users.id] }),
  monitoraggio: one(monitoraggi, { fields: [referralClicks.monitoraggioId], references: [monitoraggi.id] }),
}));

export const referralBookingsRelations = relations(referralBookings, ({ one }) => ({
  click: one(referralClicks, { fields: [referralBookings.clickId], references: [referralClicks.id] }),
  user: one(users, { fields: [referralBookings.userId], references: [users.id] }),
}));

export const telegramUsersRelations = relations(telegramUsers, ({ one }) => ({
  user: one(users, { fields: [telegramUsers.userId], references: [users.id] }),
}));

// ---------------------------------------------------------------------------
// Inferred types for use across the monorepo
// ---------------------------------------------------------------------------

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;

export type Monitoraggio = typeof monitoraggi.$inferSelect;
export type NewMonitoraggio = typeof monitoraggi.$inferInsert;

export type PriceHistoryRecord = typeof priceHistory.$inferSelect;
export type NewPriceHistoryRecord = typeof priceHistory.$inferInsert;

export type Notification = typeof notifications.$inferSelect;
export type NewNotification = typeof notifications.$inferInsert;

export type Subscription = typeof subscriptions.$inferSelect;
export type NewSubscription = typeof subscriptions.$inferInsert;

export type DiscountCode = typeof discountCodes.$inferSelect;
export type NewDiscountCode = typeof discountCodes.$inferInsert;

export type DiscountRedemption = typeof discountRedemptions.$inferSelect;
export type NewDiscountRedemption = typeof discountRedemptions.$inferInsert;

export type ErrorLog = typeof errorLogs.$inferSelect;
export type NewErrorLog = typeof errorLogs.$inferInsert;

export type ReferralClick = typeof referralClicks.$inferSelect;
export type NewReferralClick = typeof referralClicks.$inferInsert;
export type ReferralBooking = typeof referralBookings.$inferSelect;
export type NewReferralBooking = typeof referralBookings.$inferInsert;

export type Invite = typeof invites.$inferSelect;
export type NewInvite = typeof invites.$inferInsert;

export type TelegramUser = typeof telegramUsers.$inferSelect;
export type NewTelegramUser = typeof telegramUsers.$inferInsert;
